import {
  CalendarCheck,
  type LucideIcon,
  PhoneOutgoing,
  UserPlus,
  UserRoundCheck,
} from "lucide-react"

import { cn } from "@/lib/utils"

import { type AudioSample, AudioSamplePlayer } from "./audio-sample-player"
import { ChatDemo } from "./chat-demo"
import { Eyebrow } from "./eyebrow"
import { MeshFrame } from "./mesh-frame"
import { type JobOffer } from "./types"

interface Step {
  icon: LucideIcon
  title: string
  detail: string
}

interface Job {
  /** The archetype slug, so a block pairs with its serialised offer. */
  slug: string
  label: string
  headline: string
  sub: string
  /** Present only for jobs whose graphic is the step-trace window. */
  windowTag?: string
  steps?: Step[]
}

/* Real call recordings, one per industry, reused from imperative-website. */
const SAMPLES: AudioSample[] = [
  {
    id: "hvac",
    label: "HVAC",
    src: "/audio/receptionist-hvac.m4a",
    scenario: "After-hours call handles emergency bookings",
  },
  {
    id: "law",
    label: "Personal Injury",
    src: "/audio/receptionist-personal-injury.m4a",
    scenario: "Lead captured over the weekend",
  },
  {
    id: "realestate",
    label: "Real Estate",
    src: "/audio/receptionist-real-estate.m4a",
    scenario: "Seller qualified and handed to a listing agent",
  },
]

/**
 * One block per job, because the jobs are the SKUs. Same shape every time:
 * plain claim on one side, a step-by-step trace of the job being done on the
 * other. Copy stays neutral — the trace is the persuasion, not the voice.
 */
const JOBS: Job[] = [
  {
    slug: "receptionist",
    label: "Receptionist",
    headline: "Your phone, answered around the clock",
    sub: "It picks up when you can’t, answers questions from your business info, and books appointments straight into your calendar.",
  },
  {
    slug: "website-chat",
    label: "Website chat",
    headline: "Every website visitor gets a greeting",
    sub: "It answers visitors' questions in your words, captures their contact details, and turns quick questions into leads and bookings.",
  },
  {
    slug: "lead-responder",
    label: "Lead responder",
    headline: "New leads followed up while they’re warm",
    sub: "When someone fills in a form or requests a quote, the agent calls them back, confirms the details, and gets them on your calendar.",
    windowTag: "Website form · 9:12 AM",
    steps: [
      {
        icon: UserPlus,
        title: "Lead received",
        detail: "Requesting quote",
      },
      {
        icon: PhoneOutgoing,
        title: "Outbound call",
        detail: "47 seconds later",
      },
      {
        icon: UserRoundCheck,
        title: "Lead qualified",
        detail: "Budget, timing, requirements aligned",
      },
      {
        icon: CalendarCheck,
        title: "Booked",
        detail: "Friday 10:00 AM",
      },
    ],
  },
]

function JobWindow({ job }: { job: Job }) {
  const steps = job.steps ?? []
  return (
    <div className="border-lp-fg/10 overflow-hidden rounded-2xl border bg-white shadow-[0_24px_60px_rgba(23,23,23,0.18)]">
      <div className="border-lp-border/70 flex items-center gap-2.5 border-b px-5 py-3">
        <span
          className="size-2 rounded-full"
          style={{ background: "var(--lp-accent)" }}
        />
        <span className="text-lp-fg text-sm font-semibold">Front desk</span>
        <span className="text-lp-muted/70 ml-auto text-xs">
          {job.windowTag}
        </span>
      </div>

      <div className="px-5 py-4 sm:px-6">
        {steps.map((step, i) => (
          <div key={step.title} className="flex gap-3.5">
            {/* icon + connector */}
            <div className="flex flex-col items-center">
              <span className="bg-lp-surface text-lp-fg grid size-9 shrink-0 place-items-center rounded-full">
                <step.icon className="size-4" />
              </span>
              {i < steps.length - 1 && (
                <span className="bg-lp-border w-px flex-1" />
              )}
            </div>

            <div className={cn("min-w-0", i < steps.length - 1 && "pb-5")}>
              <div className="flex items-center gap-2 pt-1.5">
                <span
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ background: "var(--lp-accent)" }}
                />
                <span className="text-lp-fg text-sm font-medium">
                  {step.title}
                </span>
              </div>
              <p className="text-lp-muted mt-0.5 truncate pl-3.5 text-sm">
                {step.detail}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function JobBlocks({
  offers,
  onHire,
}: {
  /** Serialised offers keyed by archetype slug. */
  offers: Record<string, JobOffer>
  /** Opens the job's purchasing modal; absent offer → no action rendered. */
  onHire: (offer: JobOffer) => void
}) {
  return (
    <section id="jobs" className="w-full px-4 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto w-full max-w-5xl">
        <div className="flex flex-col items-center text-center">
          <Eyebrow>The jobs</Eyebrow>
          <h2 className="font-lp-serif text-lp-fg mt-3 text-[clamp(1.625rem,2.8vw,2.125rem)] leading-[1.1] tracking-[-0.02em]">
            One front desk, every conversation covered
          </h2>
        </div>

        <div className="mt-16 flex flex-col gap-20 sm:mt-20 sm:gap-28">
          {JOBS.map((job, i) => {
            const offer = offers[job.slug]
            // IMP-297 + IMP-498: a job is hireable only when the partner has
            // priced it AND sells through the self-serve flow. Otherwise the
            // block still makes its argument and the closing panel's Contact us
            // carries the gesture — better than a button that can't transact.
            const pricing = offer?.pricing ?? null
            const trial = pricing?.trial ?? null

            return (
              <div
                key={job.slug}
                className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
              >
                <div className={cn("min-w-0", i % 2 === 1 && "lg:order-2")}>
                  <Eyebrow>{job.label}</Eyebrow>

                  <h3 className="font-lp-serif text-lp-fg mt-5 max-w-[20ch] text-[clamp(1.5rem,2.6vw,2rem)] leading-[1.12] tracking-[-0.02em] text-balance">
                    {job.headline}
                  </h3>

                  <p className="text-lp-muted mt-4 max-w-[46ch] text-base leading-[1.65] text-pretty">
                    {job.sub}
                  </p>

                  {offer && pricing && (
                    <div className="mt-7 flex flex-col items-start gap-3">
                      <button
                        type="button"
                        onClick={() => onHire(offer)}
                        className="bg-lp-fg inline-flex h-11 items-center rounded-full px-6 text-sm font-semibold text-white transition-colors select-none hover:bg-black"
                      >
                        {trial ? `Try ${job.label} free` : `Hire ${job.label}`}
                      </button>
                      {/* Trial messaging lives here and nowhere else on the
                          page — it belongs to the one job it's configured on. */}
                      {trial && (
                        <p className="text-lp-muted text-sm">
                          {trial.days}-day free trial, then billed monthly.
                          Cancel anytime.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className={cn("min-w-0", i % 2 === 1 && "lg:order-1")}>
                  <MeshFrame>
                    {job.slug === "receptionist" ? (
                      <AudioSamplePlayer samples={SAMPLES} />
                    ) : job.slug === "website-chat" ? (
                      <ChatDemo />
                    ) : (
                      <JobWindow job={job} />
                    )}
                  </MeshFrame>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
